import BootIconButton from "components/common/custom/IconButton";
import { motion } from "framer-motion";
import { getToday } from "helpers/utils";
import moment from "moment";
import { Card, Col, Row } from "react-bootstrap";
import { BsDownload, BsEye } from "react-icons/bs";
import { useSelector } from "react-redux";

const ClaimProviderClinical = () => {
  return (
    <div className="d-flex flex-column gap-3">
      <InvestigationDetails />
      <AttachmentDetails />
    </div>
  );
};

export default ClaimProviderClinical;

const InvestigationDetails = () => {
  const { viewClaimInfo } = useSelector((state) => state.claim);

  const supInfo = viewClaimInfo?.payload_json?.sup_info || {};

  const supportingInfo = Object.values(supInfo);

  const additionalInformation = supportingInfo?.find(
    (item) => item?.addinfo
  )?.addinfo;

  // Find the investigation result
  const investigation = supportingInfo?.find((item) => item?.invres)?.invres;

  const investigationName = viewClaimInfo?.provider_request?.clinical_documentation?.[0]?.investigationresult?.name

  return (
    <motion.div
      style={{ border: "2px solid #d9f8eb" }}
      className="rounded p-3 position-relative mt-3"
      initial={{ opacity: 0, y: 50 }} // Starts faded out and moved down
      animate={{ opacity: 1, y: 0 }} // Fades in and moves to place
      exit={{ opacity: 0, y: 50 }} // Optional: for exit animation
      transition={{ duration: 0.5 }} // Smooth transition
    >
      {/* Floating Badge Header */}
      <div className="position-absolute top-0 start-40 translate-middle-y px-2">
        <span className="badge shadow-sm d-flex align-items-center justify-content-center gap-2 badge-subtle-success">
          <span className="fs-11">Investigation Details</span>
        </span>
      </div>
      <Row>
        <Col xs={12}>
          {additionalInformation && (
            <>
              <Row>
                <Col xs={12} md={3}>
                  <span className="fs-10 fw-bold">Additional Info</span>
                </Col>
                <Col xs={12} md={9}>
                  <span className="fs-10 fw-normal text-600">
                    {additionalInformation}
                  </span>
                </Col>
              </Row>
              <hr />
            </>
          )}
          <Row>
            <Col xs={12} md={3}>
              <span className="fs-10 fw-bold">Additional Supporting Info</span>
            </Col>
            <Col xs={12} md={9}>
              <span className="fs-10 fw-normal text-600">
                {investigationName}
              </span>
            </Col>
          </Row>
          <hr />

          {investigation?.code == "other" && (
            <Row>
              <Col xs={12} md={3}>
                <span className="fs-10 fw-bold">Other Description</span>
              </Col>
              <Col xs={12} md={9}>
                <span className="fs-10 fw-normal text-600">
                  {investigation?.oth}
                </span>
              </Col>
            </Row>
          )}
        </Col>
      </Row>
    </motion.div>
  );
};

const AttachmentDetails = () => {
  const { viewClaimInfo } = useSelector((state) => state.claim);

  // Extract sup_info and sup_info_attch
  const supInfo = viewClaimInfo?.payload_json?.sup_info || {};
  const supInfoAttch = viewClaimInfo?.payload_json?.sup_info_attch || {};

  // Extract invres from sup_info (where it exists and has actual attachment data)
  const supInfoEntries = Object.entries(supInfo || {});
  const invresAttachments = supInfoEntries
    .filter(([key, value]) => {
      const invres = value?.invres;
      // Only include if invres exists and has actual attachment data
      // Must have either data (base64) or url, and a title/filename
      return (
        invres &&
        (invres.data || invres.url) && // Must have data or URL
        (invres.title || invres.filename) // Must have a name
      );
    })
    .map(([key, value]) => ({
      ...value.invres,
      source: "invres",
    }));

  // Extract attachments from sup_info_attch (only those with actual data)
  const supInfoAttchEntries = Object.entries(supInfoAttch || {});
  const supInfoAttchList = supInfoAttchEntries
    .filter(([key, value]) => {
      // Only include if it has actual attachment data
      // Must have either data (base64) or url, and a title/filename
      return (
        value &&
        (value.data || value.url) && // Must have data or URL
        (value.title || value.filename) // Must have a name
      );
    })
    .map(([key, value]) => ({
      ...value,
      source: "sup_info_attch",
    }));

  // Extract invoice and CAF attachments from viewClaimInfo
  const invoiceAttach = viewClaimInfo?.invoice_attach
    ? {
      url: viewClaimInfo.invoice_attach,
      title: viewClaimInfo.invoice_attach.split("/").pop() || "Invoice.pdf",
      contentType: "application/pdf",
      source: "invoice_attach",
    }
    : null;

  const cafAttach = viewClaimInfo?.caf_attach
    ? {
      url: viewClaimInfo.caf_attach,
      title: viewClaimInfo.caf_attach.split("/").pop() || "CAF.pdf",
      contentType: "application/pdf",
      source: "caf_attach",
    }
    : null;

  // Combine all attachments (only those with actual data)
  const allAttachments = [
    ...invresAttachments,
    ...supInfoAttchList,
    ...(invoiceAttach ? [invoiceAttach] : []),
    ...(cafAttach ? [cafAttach] : []),
  ];

  // Extract file name from attachment
  const getFileName = (attachment) => {
    return attachment.title || attachment.filename || "Unknown File";
  };

  // Get file type from file name or contentType
  const getFileType = (attachment) => {
    const fileName = getFileName(attachment);
    const extension = fileName.split(".").pop()?.toUpperCase();
    if (extension && extension !== fileName) {
      return extension;
    }
    // Try to get from contentType
    if (attachment.contentType) {
      const mimeParts = attachment.contentType.split("/");
      return mimeParts[1]?.toUpperCase() || "FILE";
    }
    return "FILE";
  };

  // Handle download for base64 attachments and URLs
  const handleDownload = async (attachment) => {
    // Handle URL-based attachments (invoice_attach, caf_attach)
    if (attachment.url && !attachment.data) {
      try {
        const response = await fetch(attachment.url);
        const blob = await response.blob();

        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = getFileName(attachment);
        link.click();
      } catch (error) {
        console.error("Error downloading file from URL:", error);
        // Fallback: open in new tab
        window.open(attachment.url, "_blank");
      }
      return;
    }

    // Handle base64 attachments
    if (!attachment?.data) {
      console.error("No data available for download");
      return;
    }

    try {
      // Decode base64 data
      const byteCharacters = atob(attachment.data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const contentType = attachment.contentType || "application/pdf";
      const blob = new Blob([byteArray], { type: contentType });

      // Create temporary link and trigger download
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = getFileName(attachment);
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading file:", error);
    }
  };

  // Handle view for base64 attachments and URLs
  const handleView = (attachment) => {
    // Handle URL-based attachments (invoice_attach, caf_attach)
    if (attachment.url && !attachment.data) {
      window.open(attachment.url, "_blank");
      return;
    }

    // Handle base64 attachments
    if (!attachment?.data) {
      console.error("No data available for viewing");
      return;
    }

    try {
      const contentType = attachment.contentType || "application/pdf";
      const dataUrl = `data:${contentType};base64,${attachment.data}`;
      const newWindow = window.open();
      if (newWindow) {
        newWindow.document.write(`
          <html>
            <head><title>${getFileName(attachment)}</title></head>
            <body style="margin:0; padding:0;">
              ${contentType.includes("pdf")
            ? `<embed src="${dataUrl}" type="${contentType}" width="100%" height="100%" />`
            : `<img src="${dataUrl}" style="width:100%; height:auto; max-width:100%;" alt="Preview" />`
          }
            </body>
          </html>
        `);
        newWindow.document.close();
      }
    } catch (error) {
      console.error("Error viewing file:", error);
    }
  };

  if (!allAttachments?.length) {
    return null;
  }

  return (
    <motion.div
      style={{ border: "2px solid #d9f8eb" }}
      className="rounded p-3 position-relative mt-3"
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="position-absolute top-0 start-40 translate-middle-y px-2">
        <span className="badge shadow-sm d-flex align-items-center justify-content-center gap-2 badge-subtle-success">
          <span className="fs-11">Attachment Details</span>
        </span>
      </div>

      <Row className="mt-4">
        {allAttachments.map((attachment, index) => {
          const fileName = getFileName(attachment);
          const fileType = getFileType(attachment);
          const creationDate = attachment.creation
            ? moment(attachment.creation).format("DD-MM-YYYY")
            : moment(getToday()).format("DD-MM-YYYY");

          return (
            <Col xs={12} key={index} className="mb-3">
              <Card className="p-3 rounded border-0 shadow-sm">
                <Row className="align-items-center">
                  {/* Number circle */}
                  <Col xs="auto">
                    <div
                      className="d-flex align-items-center justify-content-center rounded-circle border"
                      style={{
                        width: "40px",
                        height: "40px",
                        color: "#F59E0B",
                        borderColor: "#10B981",
                        boxShadow: "0px 2px 6px rgba(0,0,0,0.1)",
                      }}
                    >
                      <strong>{index + 1}</strong>
                    </div>
                  </Col>

                  {/* File details */}
                  <Col
                    xs={10}
                    md
                    className="d-flex flex-wrap justify-content-between align-items-center mt-3 mt-md-0 gap-3"
                  >
                    <div className="d-flex flex-column">
                      <small className="text-muted fw-bold mb-1">
                        Document Name
                      </small>
                      <small className="text-600">{fileName}</small>
                    </div>

                    <div className="d-flex flex-column">
                      <small className="text-muted fw-bold mb-1">
                        Document Type
                      </small>
                      <small className="text-600">{fileType}</small>
                    </div>

                    <div className="d-flex flex-column">
                      <small className="text-muted fw-bold mb-1">
                        Creation Date
                      </small>
                      <small className="text-600">{creationDate}</small>
                    </div>

                    {/* Action buttons */}
                    <Col xs="auto" className="d-flex gap-2">
                      {(attachment.data || attachment.url) && (
                        <>
                          <BootIconButton
                            variant="info"
                            className="rounded-circle d-flex align-items-center justify-content-center"
                            style={{ width: "30px", height: "30px" }}
                            onClick={() => handleView(attachment)}
                            title="View Document"
                          >
                            <BsEye size={18} color="#fff" />
                          </BootIconButton>
                          <BootIconButton
                            variant="success"
                            className="rounded-circle d-flex align-items-center justify-content-center"
                            style={{ width: "30px", height: "30px" }}
                            onClick={() => handleDownload(attachment)}
                            title="Download Document"
                          >
                            <BsDownload size={18} color="#fff" />
                          </BootIconButton>
                        </>
                      )}
                    </Col>
                  </Col>
                </Row>
              </Card>
            </Col>
          );
        })}
      </Row>
    </motion.div>
  );
};
